import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { useFeatureEntitlements } from "@frontegg/react";
import { setNestedObjectValues } from "formik";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import { Avatar, IconButton, ListItemIcon, PageHeader } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  BubbleChatIcon,
  Building03Icon,
  Edit04Icon,
  MailUpload01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import ContactType from "enums/ContactType";
import { ParentType } from "enums/Comments";
import useContactData from "hooks/useContactData";
import { useComments } from "hooks/useComments";
import CommentsPanel from "components/common/comments/CommentsPanel";
import { GET_CONTACT, GET_INDIVIDUALS_FOR_BUSINESS } from "graphql/contact";
import ConfigureAddressDrawer from "components/common/ConfigureAddressDrawer";
import IdNotFoundPage from "components/common/IdNotFoundPage";
import { toastMutationError } from "utils/toastMutationError";
import ContactDrawerRouter from "components/admin/contacts/ContactDrawerRouter";
import { InviteContactModal } from "components/admin/contacts/ContactModules";
import { ContactRouterDrawerType } from "enums/ContactRouterDrawerType";
import RenderRelationship from "components/common/contacts/RenderRelationship";
import ContactDetailsCard from "components/admin/contacts/detail/ContactDetailsCard";
import ContactPeopleCard, {
  type LinkedIndividual,
} from "components/admin/contacts/detail/ContactPeopleCard";
import ContactBusinessCard from "components/admin/contacts/detail/ContactBusinessCard";
import ContactAttachmentsCard from "components/admin/contacts/detail/ContactAttachmentsCard";
import ContactRecentPaymentsCard from "components/admin/contacts/detail/ContactRecentPaymentsCard";
import RecurringBillingPanel from "components/admin/contacts/RecurringBillingPanel";
import SubscriptionDetailsDrawer from "components/admin/contacts/SubscriptionDetailsDrawer";
import { type ContactSubscription } from "utils/contacts/recurringBilling";
import { useContactSubscriptions } from "hooks/useContactSubscriptions";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";

const ContactDetails: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contactId = "" } = useParams<{ contactId: string }>();
  const { isEntitled: featurePayments } =
    useFeatureEntitlements("feature_payments");

  const [editing, setEditing] = useState(false);
  const [openAddressDrawer, setOpenAddressDrawer] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<ContactSubscription | null>(null);
  const [addingPerson, setAddingPerson] = useState(false);
  const [openInviteContactModal, setOpenInviteContactModal] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);

  const theme = useTheme();
  const commentsDockable = useMediaQuery(theme.breakpoints.up("md"));
  const commentActions = useComments(contactId, ParentType.CONTACT);
  const commentCount = commentActions.comments?.length ?? 0;

  const {
    formikContact,
    specificLoadingStates,
    handleUpdateContact,
    refetchContact,
  } = useContactData({ contactId });

  const contact = formikContact.values;
  const isBusiness = contact.contactType === ContactType.Business;

  const {
    data: peopleData,
    loading: loadingPeople,
    refetch: refetchPeople,
  } = useQuery(GET_INDIVIDUALS_FOR_BUSINESS, {
    variables: { contactId, status: "ACTIVE" },
    skip: !isBusiness || !contactId,
    fetchPolicy: "cache-and-network",
  });

  const people: LinkedIndividual[] = peopleData?.individualsForBusiness ?? [];

  // The individual's own record carries parentContactId and businessName but
  // not the business's address, and the card names where that business is.
  const { data: parentData } = useQuery(GET_CONTACT, {
    variables: { id: contact.parentContactId },
    skip: isBusiness || !contact.parentContactId,
  });
  const parentAddress = parentData?.contact?.address;
  const parentLocation = parentAddress?.city
    ? `${parentAddress.city}, ${parentAddress.state}`
    : "";

  const { subscriptions, monthlyValueCents, connected } =
    useContactSubscriptions(contactId);

  const displayName = isBusiness
    ? (contact.businessName ?? "")
    : [contact.firstName, contact.lastName].filter(Boolean).join(" ");

  const location = contact.address?.city
    ? `${contact.address.city}, ${contact.address.state}`
    : "";

  const backToContacts = () => navigate("/admin/contacts");

  const handleSave = async () => {
    const errors = await formikContact.validateForm();

    if (Object.keys(errors).length !== 0) {
      formikContact.setTouched(setNestedObjectValues(errors, true));
      toast.error(t("Please complete the required fields"));
      return;
    }

    try {
      const result = await handleUpdateContact(contact);
      if (!result) {
        toast.error(t("Failed to update contact"));
        return;
      }

      await refetchContact();
      toast.success(t("Contact updated successfully"));
      setEditing(false);
    } catch (err: unknown) {
      toastMutationError(err, t("Failed to update contact"));
    }
  };

  const handleCancel = () => {
    formikContact.resetForm();
    setEditing(false);
  };

  if (specificLoadingStates.loadingContact || !contact.id) {
    return (
      <IdNotFoundPage
        title={t("Contact")}
        isLoading={specificLoadingStates.loadingContact}
        showSkeleton
      />
    );
  }

  const commentsToggle = (
    <IconButton
      variant="ghost"
      aria-label={t("Comments")}
      onClick={() => setCommentsOpen((wasOpen) => !wasOpen)}
    >
      <Badge badgeContent={commentCount} color="primary">
        <HugeiconsIcon icon={BubbleChatIcon} size={18} />
      </Badge>
    </IconButton>
  );

  return (
    <Stack gap="var(--space-5)" sx={{ padding: "var(--space-6)" }}>
      <PageHeader
        sx={{ gap: "var(--space-5)" }}
        breadcrumbs={[
          {
            label: t("Contacts"),
            href: "/admin/contacts",
            onClick: backToContacts,
          },
          { label: displayName },
        ]}
        back={{ label: t("Back to contacts"), onClick: backToContacts }}
        title={
          <Inline gap="var(--space-4)" align="center" wrap={false}>
            {isBusiness ? (
              <ListItemIcon wrapper="brand">
                <HugeiconsIcon
                  icon={Building03Icon}
                  size={22}
                  color="var(--color-text-brand)"
                />
              </ListItemIcon>
            ) : (
              <Avatar size="xl" name={displayName} />
            )}
            <Stack gap="var(--space-2)">
              <Typography variant="h3" component="span">
                {displayName}
              </Typography>
              <Inline gap="var(--space-3)" align="center">
                <RenderRelationship relationship={contact.relationshipType} />
                <Typography
                  variant="caption"
                  sx={{ color: "var(--color-text-tertiary)" }}
                >
                  {isBusiness ? location : contact.title}
                </Typography>
              </Inline>
            </Stack>
          </Inline>
        }
        actions={
          editing ? (
            <>
              {commentsToggle}
              <Button variant="primary-outlined" onClick={handleCancel}>
                {t("Cancel")}
              </Button>
              <Button
                variant="primary-filled"
                onClick={handleSave}
                disabled={specificLoadingStates.updatingContact}
              >
                {t("Save")}
              </Button>
            </>
          ) : (
            <>
              {commentsToggle}
              <Button
                variant="default-outlined"
                startIcon={<HugeiconsIcon icon={MailUpload01Icon} size={16} />}
                onClick={() => setOpenInviteContactModal(true)}
              >
                {t("Send Invite")}
              </Button>
              <Button
                variant="default-outlined"
                startIcon={<HugeiconsIcon icon={Edit04Icon} size={16} />}
                onClick={() => setEditing(true)}
              >
                {t("Edit")}
              </Button>
            </>
          )
        }
      />

      <Columns
        count={{ xs: 1, md: "minmax(0, 1.5fr) minmax(0, 1fr)" }}
        gap="var(--space-5)"
        sx={{ alignItems: "start" }}
      >
        <Stack gap="var(--space-5)">
          <ContactDetailsCard
            formik={formikContact}
            editing={editing}
            onOpenAddressDrawer={() => setOpenAddressDrawer(true)}
          />

          {isBusiness ? (
            <ContactPeopleCard
              people={people}
              loading={loadingPeople}
              onAddPerson={() => setAddingPerson(true)}
              onOpenPerson={(personId) =>
                navigate(`/admin/contacts/${personId}`)
              }
            />
          ) : (
            <ContactBusinessCard
              editing={editing}
              businessName={contact.businessName ?? ""}
              businessLocation={parentLocation}
              parentContactId={contact.parentContactId ?? ""}
              onSelectBusiness={(businessId, businessName) => {
                formikContact.setFieldValue("parentContactId", businessId);
                formikContact.setFieldValue("businessName", businessName);
              }}
              onOpenBusiness={(businessId) =>
                navigate(`/admin/contacts/${businessId}`)
              }
            />
          )}

          <ContactAttachmentsCard contactId={contactId} />
        </Stack>

        <Stack gap="var(--space-5)">
          <RecurringBillingPanel
            subscriptions={subscriptions}
            monthlyValueCents={monthlyValueCents}
            connected={connected}
            onSelectSubscription={setSelectedSubscription}
          />

          {featurePayments && (
            <ContactRecentPaymentsCard contactId={contactId} />
          )}
        </Stack>
      </Columns>

      <CommentsPanel
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        docked={commentsDockable}
        commentActions={commentActions}
      />

      {addingPerson && (
        <ContactDrawerRouter
          open={addingPerson}
          drawerType={ContactRouterDrawerType.NewContact}
          startIndividualWithBusinessId={contactId}
          onClose={() => setAddingPerson(false)}
          refreshContacts={() => {
            refetchPeople();
          }}
        />
      )}

      <SubscriptionDetailsDrawer
        open={!!selectedSubscription}
        subscription={selectedSubscription}
        customerName={displayName}
        onClose={() => setSelectedSubscription(null)}
        onSave={() => toast.info(t("SubscriptionNotSavedYet"))}
        onPause={() => toast.info(t("SubscriptionActionsComingSoon"))}
        onCancel={() => toast.info(t("SubscriptionActionsComingSoon"))}
      />

      <ConfigureAddressDrawer
        open={openAddressDrawer}
        drawerTitle={[t("Configure Address")]}
        initialValues={contact.address}
        onClose={() => setOpenAddressDrawer(false)}
        onSuccess={(address) => {
          formikContact.setFieldValue("address", address);
          setOpenAddressDrawer(false);
        }}
      />

      <InviteContactModal
        openInviteContactModal={openInviteContactModal}
        setOpenInviteContactModal={setOpenInviteContactModal}
        contact={{
          id: contact.id ?? "",
          email: contact.email,
          contactType: contact.contactType,
        }}
      />
    </Stack>
  );
};

export default ContactDetails;
